// Core React Imports
import { useState, useEffect, useRef, useMemo } from "react";

// Third-Party Libraries
import { useNavigate } from "react-router-dom";

// Local Imports
import { useWidgetFilterDropdowns } from "../../widgets/dropdowns/widget-filter-dropdowns/useWidgetFilterDropdowns";
import useUtilsUnit from "../../utils/useUtilsUnit";
import ApiService from "../../data/ApiService";
import { useLoading } from "../../widgets/loadings/WidgetLoading";
import { useMapMarkersPolygons } from "../../widgets/maps/useMapMarkersPolygons";
import { seedTerminalPolygonCacheFromFilterData } from "../../utils/TerminalPolygonCache";

export function usePageFilter() {
  const navigate = useNavigate();
  const { userData } = useUtilsUnit({ userData: true });
  const { showLoading, hideLoading } = useLoading();
  const [filterData, setFilterData] = useState(null);
  const [dataIsReceived, setDataIsReceived] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);

  // useWidgetFilterDropdowns - start
  const { uniqueDistricts, uniqueMunicipalities, uniqueTerminals } = useWidgetFilterDropdowns(filterData, selectedDistrict, selectedMunicipality);
  // useWidgetFilterDropdowns - end

  // Fetch data on component mount
  useEffect(() => {
    (async () => {
      await fetchFilterData();
      setDataIsReceived(true);
    })();
  }, []);

  // Updates active filters state whenever any filter selection changes
  useEffect(() => {
    setHasActiveFilters(!!(selectedDistrict || selectedMunicipality || selectedTerminal));
  }, [selectedDistrict, selectedMunicipality, selectedTerminal]);

  // Fetch filter data function
  const fetchFilterData = async () => {
    showLoadingRef.current();

    try {
      const filterDataResponse = await ApiService.getDataForFiltering();
      seedTerminalPolygonCacheFromFilterData(filterDataResponse, 0);
      setFilterData(filterDataResponse);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // useWidgetFilterDropdowns - start
  const handleSelectDropdownDistrict = (option) => {
    setSelectedDistrict(option);
    setSelectedMunicipality(null);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownMunicipality = (option) => {
    setSelectedMunicipality(option);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownTerminal = (option) => {
    setSelectedTerminal(option);
  };
  // useWidgetFilterDropdowns - end

  // Returns either selected terminal or all terminals for map markers display
  function getMarkersMap() {
    if (selectedTerminal) {
      return [selectedTerminal];
    } else {
      return uniqueTerminals;
    }
  }

  const markersForMap = useMemo(() => {
    if (selectedTerminal) {
      return [selectedTerminal];
    }
    return uniqueTerminals;
  }, [selectedTerminal, uniqueTerminals]);

  const mapMarkers = useMapMarkersPolygons(markersForMap, {
    focusTerminalID: selectedTerminal?.terminalID ?? null,
    fetchAllMissingPolygons: true,
  });

  return {
    dataIsReceived,
    uniqueDistricts,
    handleSelectDropdownDistrict,
    selectedDistrict,
    uniqueMunicipalities,
    handleSelectDropdownMunicipality,
    selectedMunicipality,
    uniqueTerminals,
    handleSelectDropdownTerminal,
    selectedTerminal,
    getMarkersMap,
    mapMarkers,
    userData,
    navigate,
    hasActiveFilters,
  };
}
